/**
 * MEKÂN · KİŞİLER · SONRAKİ ÇEKİM.
 *
 * Three sections because there are three truths, and the reason this subject
 * type exists at all is that they must not be averaged. A room that is complete
 * beside a person photographed only from behind would score ninety per cent and
 * tell the reader nothing; there is no combined number on this screen and none
 * in the response it draws.
 *
 * ## What it says when it cannot say much
 *
 * Most of what this screen shows on a real capture today is *absence*, and
 * absence has three different causes that need three different sentences:
 *
 * * **Nothing has been analysed.** The people section is empty because the
 *   face pipeline has not run, not because the room was empty.
 * * **Nothing measures a body.** Every body part comes back `not_assessed` and
 *   the screen says so once, plainly, instead of rendering eight grey rows and
 *   letting the reader conclude the person was badly photographed.
 * * **The count is a floor.** Without a real capture order there is nothing to
 *   track, so "at least three people" is the honest statement and a ceiling
 *   would be invented.
 *
 * A screen that collapsed those into "0%" would be wrong in all three cases.
 *
 * ## No identity, ever
 *
 * "Kişi 1" is ordinal and scoped to this project. Nothing here compares a
 * person to another capture, and nothing stores anything that could.
 */

import { useCallback } from "react";

import { getSceneCoverage } from "@/api/client";
import type { CoverageStatus, SceneAction, ScenePerson } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { PageHeader } from "@/components/product";
import { Callout, Loading, StatusBadge } from "@/components/ui";
import { COVERAGE_STATUS_TERMS } from "@/i18n/terms";
import { useAsync } from "@/lib/useAsync";
import { guidedRegionLabel } from "./guidedCoverage";
import "./coverage.css";

/** The head regions, in the words the head pipeline's own screens use. */
const FACE_REGION_LABELS: Record<string, string> = {
  front_face: "Ön yüz",
  left_side: "Sol taraf",
  right_side: "Sağ taraf",
  left_ear: "Sol kulak",
  right_ear: "Sağ kulak",
};

/** Coarse on purpose: these are the parts a person can be *asked* to show. */
const BODY_PART_LABELS: Record<string, string> = {
  head: "Baş",
  torso_front: "Gövde ön",
  torso_back: "Gövde arka",
  left_side: "Sol yan",
  right_side: "Sağ yan",
  arms: "Kollar",
  legs: "Bacaklar",
  feet: "Ayaklar",
};

const personLabel = (person: string): string =>
  `Kişi ${person.replace(/^person_/, "")}`;

function StatusRow({ label, status }: { label: string; status: CoverageStatus }) {
  const term = COVERAGE_STATUS_TERMS[status];
  return (
    <li className="scene__row">
      <span>{label}</span>
      {/* The glyph is never the only carrier: the badge prints the word. */}
      <StatusBadge tone={term.tone}>{term.label}</StatusBadge>
    </li>
  );
}

/** The sentence to show, which is not always the one the server composed.
 *
 * A place-region instruction arrives as "Mekânın wall_1 bölümünü tamamlayın."
 * - the server has no Turkish label for a station, and it should not: the
 * labels are one map, they live beside the panel that draws them, and a second
 * copy in Python is the pair of literals that drift apart. So the id is
 * replaced here, from the same map the MEKÂN list above is drawn from.
 *
 * The server's own sentence is the fallback rather than an error. An action
 * for a station this build has no label for is still an instruction somebody
 * can act on, and dropping it would be worse than showing it plainly.
 */
function actionSentence(action: SceneAction): string {
  if (action.action !== "photograph_place_region" || !action.target) {
    return action.message;
  }
  const label = guidedRegionLabel("human_in_environment", action.target);
  return label ? `Mekânın ${label} bölümünü tamamlayın.` : action.message;
}

function PersonCard({ person }: { person: ScenePerson }) {
  const faceEntries = Object.entries(person.face);
  return (
    <section className="card stack-tight" aria-label={personLabel(person.person)}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>{personLabel(person.person)}</h3>
        <span className="faint">{person.frame_count} fotoğrafta görüldü</span>
      </div>

      {/*
        Before the coverage, because it changes what the coverage means: a
        moving subject reconstructs with ghosting and duplicated limbs whatever
        the region verdicts say, and the capture flow should say so before the
        reconstruction runs rather than after it fails.
      */}
      {person.moved && (
        <Callout tone="caution" title="Bu kişi çekim sırasında hareket etmiş">
          Hareket eden bir kişinin çok görüntülü modeli hayalet ve tekrarlanmış
          uzuvlar üretir. Aynı kişiyi sabit dururken yeniden çekmek sonucu
          belirgin şekilde iyileştirir.
        </Callout>
      )}

      <h4 className="scene__heading">Yüz kapsaması</h4>
      <ul className="scene__list">
        {faceEntries.map(([region, status]) => (
          <StatusRow
            key={region}
            label={FACE_REGION_LABELS[region] ?? region}
            status={status}
          />
        ))}
      </ul>

      <h4 className="scene__heading">Vücut kapsaması</h4>
      {/*
        One sentence instead of eight grey rows. Rendering the parts as
        "değerlendirilmedi" eight times reads as a badly photographed person;
        the truth is that nothing in this build looks at a body at all.
      */}
      <p className="muted">
        Bu sürümde vücut bölümleri ölçülmüyor: {Object.keys(person.body).length}{" "}
        bölüm için de bir değerlendirme üretilmedi. Bu, kişinin kötü çekildiği
        anlamına gelmez.
      </p>
    </section>
  );
}

export function SceneCoveragePanel({ projectId }: { projectId: string }) {
  const fetcher = useCallback(
    (signal: AbortSignal) => getSceneCoverage(projectId, { signal }),
    [projectId],
  );
  const { state, reload } = useAsync(fetcher);

  if (state.status === "error") {
    return <ErrorState error={state.error} onRetry={reload} />;
  }
  const scene = state.data;
  if (scene === null) return <Loading label="Sahne kapsaması yükleniyor" />;

  const placeEntries = Object.entries(scene.place);

  return (
    <div className="stack">
      <PageHeader
        title="Mekân ve Kişiler"
        subtitle="Mekân, kişiler ve sonraki çekim ayrı ayrı raporlanır."
      />

      {/*
        Said once and at the top, because it is the reason this screen looks
        different from every other coverage screen.
      */}
      <Callout tone="neutral" title="Tek bir yüzde yok">
        Mekânın kapsaması ile her kişinin kapsaması ayrı gerçeklerdir. Bunları
        tek bir orana indirmek, odası tamam olup yalnızca arkadan çekilmiş bir
        kişiyi gizler.
      </Callout>

      <section className="stack-tight" aria-label="Mekân kapsaması">
        <h2 className="scene__section">MEKÂN</h2>
        <ul className="scene__list">
          {placeEntries.map(([station, status]) => (
            <StatusRow
              key={station}
              label={
                guidedRegionLabel("human_in_environment", station) ?? station
              }
              status={status}
            />
          ))}
        </ul>
      </section>

      <section className="stack-tight" aria-label="Kişiler">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 className="scene__section">KİŞİLER</h2>
          {scene.people_summary && (
            <StatusBadge tone={scene.people_certain ? "success" : "neutral"}>
              {scene.people_summary}
            </StatusBadge>
          )}
        </div>

        {scene.analysed_frames === 0 ? (
          // Not "0 kişi". Nothing has looked, which is a different fact from
          // an empty room and calls for a different action.
          <Callout tone="neutral" title="Henüz kişi analizi çalıştırılmadı">
            Bu projedeki fotoğraflarda kimsenin olup olmadığı henüz ölçülmedi.
            Çekim ekranından analizi çalıştırdığınızda kişiler burada görünür.
          </Callout>
        ) : (
          <>
            {/*
              The count is a floor whenever the photographs have no real
              capture order, which is every upload from a gallery. Stating it
              is what stops "en az 3 kişi" being read as "3 kişi".
            */}
            {!scene.frames_ordered && (
              <p className="muted">
                Fotoğrafların çekim sırası bilinmediği için kişiler kareler
                arasında izlenemiyor. Bu sayı bir alt sınırdır: aynı karede hiç
                görünmemiş kişiler sayılamaz.
              </p>
            )}
            {scene.people_count_uncertain && scene.people.length > 0 && (
              <Callout tone="caution" title="Kişi sayısı kesinleşmedi">
                Herkesin aynı karede göründüğü bir fotoğraf, sayıyı kesinleştirir.
              </Callout>
            )}
            {scene.people.length === 0 ? (
              <p className="muted">
                Analiz edilen {scene.analysed_frames} fotoğrafta ayrı kişiler
                ayırt edilemedi.
              </p>
            ) : (
              scene.people.map((person) => (
                <PersonCard key={person.person} person={person} />
              ))
            )}
          </>
        )}
      </section>

      <section className="stack-tight" aria-label="Sonraki çekim">
        <h2 className="scene__section">SONRAKİ ÇEKİM</h2>
        {scene.actions.length === 0 ? (
          <p className="muted">
            Ölçülen eksik bir bölüm yok. Bu, her şeyin tamam olduğu anlamına
            gelmez; yalnızca ölçebildiklerimizde bir eksik görünmüyor.
          </p>
        ) : (
          // One ordered list for the whole scene, not one per person and one
          // for the room: somebody standing in a room with a phone can do one
          // thing next.
          <ol className="scene__actions">
            {scene.actions.map((action) => (
              <li key={`${action.action}-${action.person}-${action.target}`}>
                {action.person && (
                  <strong>{personLabel(action.person)}: </strong>
                )}
                {actionSentence(action)}
              </li>
            ))}
          </ol>
        )}
      </section>

      {!scene.body_coverage_measured && (
        <p className="faint">
          Vücut kapsaması bu sürümde ölçülmüyor. Yüz kapsaması, yüz analizinin
          değerlendirebildiği kareler üzerinden hesaplanır.
        </p>
      )}
    </div>
  );
}

export { BODY_PART_LABELS, FACE_REGION_LABELS, personLabel };
